import React from 'react';
import { Colors, Progress, Typography } from '@inplayer-org/inplayer-ui';
import styled, { css } from 'styled-components';
import {
  IoIosPhonePortrait,
  IoIosTabletLandscape,
  IoMdTv,
  IoLogoGameControllerB,
  IoIosHeadset,
  IoIosImage,
  IoMdClose,
  IoIosDesktop,
  IoIosCode,
} from 'react-icons/io';
import { IconType } from 'react-icons/lib/cjs';
import { ifProp } from 'styled-tools';

// types
import { AuthenticatedAudience } from 'ducks/merchantDucks/audience/types';

// utils
import calculateUserInSegmentOfTotal from 'utils/calculateUserInSegmentOfTotal';
import formatNumber from 'utils/formatNumber';

interface LabelProps {
  active?: boolean;
}

interface Props {
  data: Array<AuthenticatedAudience>;
  totalRegistrations: number;
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const ItemContainer = styled.div<LabelProps>`
  width: 90%;
  margin: 2rem;
  * {
    ${ifProp(
      'active',
      css`
        font-size: ${({ theme }) => theme.font.sizes.h5};
        font-weight: ${({ theme }) => theme.font.weights.normal};
        color: ${Colors.red};
      `,
      css`
        font-size: ${({ theme }) => theme.font.sizes.large};
        font-weight: ${({ theme }) => theme.font.weights.light};
        color: ${Colors.fontGray};
      `
    )}
  }
`;

const Label = styled(Typography)`
  color: inherit;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
`;

const InplayerIcon = styled.div<LabelProps>`
  width: 3rem;
  height: 3rem;
  padding-right: 0.3rem;
`;

const ProgressBar: React.FC<Props> = ({ data, totalRegistrations }) => {
  interface Icon {
    [key: string]: IconType;
  }

  const icons: Icon = {
    desktop: IoIosDesktop,
    API: IoIosCode,
    mobile: IoIosPhonePortrait,
    'mobile:smart': IoIosPhonePortrait,
    'mobile:feature': IoIosPhonePortrait,
    '': IoMdClose,
    tablet: IoIosTabletLandscape,
    smartTV: IoMdTv,
    television: IoMdTv,
    gaming: IoLogoGameControllerB,
    'gaming:console': IoLogoGameControllerB,
    'gaming:portable': IoLogoGameControllerB,
    'N/A': IoMdClose,
    media: IoIosImage,
    headset: IoIosHeadset,
    devboard: IoIosDesktop,
    default: IoMdClose,
  };

  return (
    <Wrapper>
      {data?.map((item, index) => {
        const Icon: IconType = icons[item.name] || icons.default;
        const active = index === 0;
        const progressColor = active ? Colors.red : Colors.darkGray;
        const percent = calculateUserInSegmentOfTotal(totalRegistrations, item.total);
        const registrationDeviceInformation = `${item.name} - ${percent}% (${formatNumber(item.total)} registrations)`;

        return (
          <ItemContainer key={item.name} active={active}>
            <LabelContainer>
              <InplayerIcon as={Icon} />
              <Label variant="p">{registrationDeviceInformation}</Label>
            </LabelContainer>

            <Progress type="line" percent={percent} strokeWidth={1.3} strokeColor={progressColor} />
          </ItemContainer>
        );
      })}
    </Wrapper>
  );
};

export default ProgressBar;
